import { useState, useEffect } from 'react';
import css from './index.module.scss';
import {
  fetchSupplierProductDocuments,
  fetchSupplierProductFilterOptions,
  fetchSupplierProductList
} from '@services/supplier.service';
import notify from '@helpers/toastify-helper';
import { ISupplierInfo } from '@helpers/types/supplier';
import ProductList from './components/product-list';
import { Chip } from '@components/base';
import SupplierProductDocuments from './components/product-documents';
import { useLocation, useNavigate } from 'react-router-dom';

interface SupplierProductListPropsType {
  supplierInfo?: ISupplierInfo;
}

export interface IProductDocumentData {
  document_type: string;
  document_description: string;
  document_file: string;
  remarks: string;
  document_name: string;
  doc_type_display: string;
  supplier_product_grade_display_name: string;
  supplier_product_grade: string;
  supplier_product_grade_document_id: string;
}

export interface IProductGradeOptions {
  product_grade_display_name: string;
  supplier_product_grade_id: string;
}

export interface IDocumentType {
  key: string;
  value: string;
}

export interface IDocumentTabState {
  isLoading: boolean;
  openAddProductModal: boolean;
  productList: any[];
  activeStep: number;
  productDocumentsList: any[];
  currentPage: number;
  documentType: IDocumentType[];
  productGradeOptions: IProductGradeOptions[];
  openAddProductDocumentModal: boolean;
  productDocumentData: IProductDocumentData | null;
  viewDocumentDescriptionData: string | null;
  itemLimit: number;
  count: number;
}

const getInitialActiveStep = () => {
  const searchParams = new URLSearchParams(location.search);
  const activeSubTab = searchParams.get('activeSubTab');
  if (!activeSubTab) return 1;
  if (activeSubTab === 'list') return 1;
  if (activeSubTab === 'document') return 2;

  return 1;
};

const getInitialPage = () => {
  const searchParams = new URLSearchParams(location.search);
  const page = searchParams.get('page');
  return page ? parseInt(page) : 1;
};

const Products = (props: SupplierProductListPropsType) => {
  const [documentTabState, setDocumentTabState] = useState<IDocumentTabState>({
    isLoading: false,
    openAddProductModal: false,
    productList: [],
    activeStep: getInitialActiveStep(),
    productDocumentsList: [],
    currentPage: getInitialPage(),
    documentType: [],
    productGradeOptions: [],
    openAddProductDocumentModal: false,
    productDocumentData: null,
    viewDocumentDescriptionData: null,
    itemLimit: 10,
    count: 0
  });

  const location = useLocation();
  const navigate = useNavigate();

  const { activeStep } = documentTabState;

  useEffect(() => {
    getProductList();
  }, []);

  const getProductDocumentsList = async (params: any) => {
    setDocumentTabState((prevState: IDocumentTabState) => ({ ...prevState, isLoading: true }));
    const productDocumentsList = await fetchSupplierProductDocuments(
      props.supplierInfo?.supplier_id || '',
      params
    );
    setDocumentTabState((prevState: IDocumentTabState) => ({ ...prevState, isLoading: false }));
    if (productDocumentsList?.success) {
      setDocumentTabState((prevState: IDocumentTabState) => ({
        ...prevState,
        productDocumentsList: productDocumentsList?.data?.results,
        count: productDocumentsList?.data?.count,
        isLoading: false
      }));
    } else {
      setDocumentTabState((prevState: IDocumentTabState) => ({ ...prevState, isLoading: false }));
      notify({ message: 'Unable to fetch Product list, Contact Tech Team', severity: 'error' });
    }
  };

  const getProductDocumentFilterOptions = async () => {
    const result = await fetchSupplierProductFilterOptions(props.supplierInfo?.supplier_id || '');
    if (result?.success) {
      const { data } = result;
      setDocumentTabState((prevState: IDocumentTabState) => ({
        ...prevState,
        productGradeOptions: data.supplier_product_grades,
        documentType: data.document_types
      }));
    } else {
      notify({ message: 'Unable to fetch Product list, Contact Tech Team', severity: 'error' });
    }
  };

  const getProductList = async () => {
    setDocumentTabState((prevState: IDocumentTabState) => ({ ...prevState, isLoading: true }));
    const productList = await fetchSupplierProductList({
      supplier_id: props.supplierInfo?.supplier_id
    });
    setDocumentTabState((prevState: IDocumentTabState) => ({ ...prevState, isLoading: false }));
    if (productList?.success) {
      setDocumentTabState((prevState: IDocumentTabState) => ({
        ...prevState,
        productList: productList?.data?.results
      }));
    } else {
      notify({ message: 'Unable to fetch Product list, Contact Tech Team', severity: 'error' });
    }
  };

  const changeTab = (step: number) => {
    setDocumentTabState((prevState: IDocumentTabState) => ({ ...prevState, activeStep: step }));

    const searchParams = new URLSearchParams(location.search);

    if (step === 1) {
      searchParams.set('activeSubTab', 'list');
    } else if (step === 2) {
      searchParams.set('activeSubTab', 'document');
    }

    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString()
      },
      { replace: true }
    );
  };

  return (
    <div className={css.mainWrapper}>
      <div className={css.taskChipWrapper}>
        <Chip
          label="Product List"
          variant={activeStep == 1 ? `filled` : `outlined`}
          onClick={() => changeTab(1)}
        />
        <Chip
          label="Documents"
          variant={activeStep == 2 ? `filled` : `outlined`}
          onClick={() => changeTab(2)}
        />
      </div>
      {activeStep === 1 && (
        <ProductList
          documentTabState={documentTabState}
          getProductList={getProductList}
          setDocumentTabState={setDocumentTabState}
        />
      )}
      {activeStep === 2 && (
        <SupplierProductDocuments
          getProductDocumentsList={getProductDocumentsList}
          getProductDocumentFilterOptions={getProductDocumentFilterOptions}
          setDocumentTabState={setDocumentTabState}
          documentTabState={documentTabState}
        />
      )}
    </div>
  );
};

export default Products;
